/**
 * Decorator that provides a default value for class properties. If the property is *undefined*, set it to the default value.
 * 
 * @example
 * In this example, the *author* property is not provided to ChildComponent. Therefore, it will be set to the default value "Anonymous".
 * 
 * ```ts
 * @Component({
 *   selector: 'app-child',
 *   template: `
 *     File name: {{fileName}}
 *     Author: {{author}}
 *   `
 * })
 * class ChildComponent {
 *   // If 'fileName' is not provided, set it to "Untitled".
 *   *@Input()* *@DefaultTo("Untitled")* fileName: string;
 * 
 *   // If 'author' is not provided, set it to "Anonymous".
 *   *@Input()* *@DefaultTo("Anonymous")* author: string;
 *
 * }
 *
 * @Component({
 *   selector: 'app-parent',
 *   template: `
 *     <app-child [fileName]="Document 1"></app-child>
 *   `
 * })
 * class ParentComponent {}
 * ```
 * 
 * From the example above, only the *fileName* property is provided to ChildComponent. Therefore, the output will be:
 * ```html
 * File name: Document 1
 * Author: Anonymous
 * ```
 */
export function DefaultTo<T>(defaultValue: T): PropertyDecorator {
   return (target: T, propertyKey: string) => {
      // Store the original key in a symbol so we can access it without overwriting the actual property.
      const symKey = Symbol(propertyKey);

      Object.defineProperty(target, propertyKey, {
         set(value) {
            // If value is not undefined, set it to the symbol property.
            this[symKey] = value !== undefined ? value : defaultValue;
         },
         get() {
            // If symbol property has a value, return it. Otherwise, return the default value.
            return this[symKey] !== undefined ? this[symKey] : defaultValue;
         },
         configurable: true,
         enumerable: true,
      });
   };
}
